import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, FileText, Mail, Settings, X } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/useToast';
import { useForm } from 'react-hook-form';

interface AdvancedExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reportData: any;
  reportType: string;
  onExport: (options: ExportOptions) => Promise<void>;
}

interface ExportOptions {
  format: 'EXCEL' | 'CSV' | 'JSON' | 'PDF';
  includeCharts: boolean;
  includeSummary: boolean;
  includeRawData: boolean;
  customFileName?: string;
  emailOptions?: {
    enabled: boolean;
    recipients: string[];
    subject?: string;
    message?: string;
  };
  filterOptions?: {
    columns: string[];
    dateRange?: {
      from: string;
      to: string;
    };
  };
}

const AdvancedExportDialog: React.FC<AdvancedExportDialogProps> = ({
  isOpen,
  onClose,
  reportData,
  reportType,
  onExport,
}) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ExportOptions>({
    defaultValues: {
      format: 'EXCEL',
      includeCharts: true,
      includeSummary: true,
      includeRawData: true,
      customFileName: `${reportType.toLowerCase()}-report-${new Date().toISOString().split('T')[0]}`,
      emailOptions: {
        enabled: false,
        recipients: [],
        subject: `${reportType} Report - ${new Date().toLocaleDateString()}`,
        message:
          'Please find the attached report generated from the Courier Management System.',
      },
      filterOptions: {
        columns: reportData?.headers || [],
      },
    },
  });

  const watchedValues = watch();

  const onSubmit = async (data: ExportOptions) => {
    try {
      const exportOptions: ExportOptions = {
        ...data,
        emailOptions: {
          ...data.emailOptions,
          enabled: data.emailOptions?.enabled || false,
          recipients: emailRecipients,
        },
      };

      await onExport(exportOptions);

      toast({
        title: 'Export Started',
        description: 'Your report export has been initiated.',
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'Failed to export report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const addEmailRecipient = () => {
    const email = recipientInput.trim();
    if (email && isValidEmail(email) && !emailRecipients.includes(email)) {
      setEmailRecipients([...emailRecipients, email]);
      setRecipientInput('');
    }
  };

  const removeEmailRecipient = (emailToRemove: string) => {
    setEmailRecipients(
      emailRecipients.filter(email => email !== emailToRemove)
    );
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getFormatDescription = (format: string): string => {
    switch (format) {
      case 'EXCEL':
        return 'Microsoft Excel format with multiple sheets and formatting';
      case 'CSV':
        return 'Comma-separated values format for data analysis';
      case 'JSON':
        return 'JavaScript Object Notation for API integration';
      case 'PDF':
        return 'Portable Document Format for sharing and printing';
      default:
        return '';
    }
  };

  const availableColumns = reportData?.headers || [];

  const steps = [
    {
      id: 1,
      title: 'Format & Content',
      description: 'Choose export format and content options',
    },
    {
      id: 2,
      title: 'Data Selection',
      description: 'Select columns and apply filters',
    },
    {
      id: 3,
      title: 'Delivery Options',
      description: 'Configure email delivery and file naming',
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Settings className='h-5 w-5' />
            Advanced Export Options
          </DialogTitle>
          <DialogDescription>
            Configure detailed export settings for your{' '}
            {reportType.toLowerCase()} report
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className='flex items-center justify-between mb-6'>
          {steps.map((step, index) => (
            <div key={step.id} className='flex items-center'>
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  currentStep >= step.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {step.id}
              </div>
              <div className='ml-3 hidden sm:block'>
                <div className='text-sm font-medium'>{step.title}</div>
                <div className='text-xs text-muted-foreground'>
                  {step.description}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-12 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          {/* Step 1: Format & Content */}
          {currentStep === 1 && (
            <div className='space-y-4'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg flex items-center gap-2'>
                    <FileText className='h-5 w-5' />
                    Export Format
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div>
                    <Label htmlFor='format'>File Format *</Label>
                    <Select
                      value={watchedValues.format}
                      onValueChange={value => setValue('format', value as any)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select format' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='EXCEL'>Excel (.xlsx)</SelectItem>
                        <SelectItem value='CSV'>CSV (.csv)</SelectItem>
                        <SelectItem value='JSON'>JSON (.json)</SelectItem>
                        <SelectItem value='PDF'>PDF (.pdf)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {getFormatDescription(watchedValues.format)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Content Options</CardTitle>
                  <CardDescription>
                    Choose what to include in your export
                  </CardDescription>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <Label htmlFor='includeCharts'>Include Charts</Label>
                        <p className='text-xs text-muted-foreground'>
                          Export visualization charts as images
                        </p>
                      </div>
                      <Switch
                        id='includeCharts'
                        checked={watchedValues.includeCharts}
                        onCheckedChange={checked =>
                          setValue('includeCharts', checked)
                        }
                      />
                    </div>

                    <div className='flex items-center justify-between'>
                      <div>
                        <Label htmlFor='includeSummary'>Include Summary</Label>
                        <p className='text-xs text-muted-foreground'>
                          Export summary statistics and metrics
                        </p>
                      </div>
                      <Switch
                        id='includeSummary'
                        checked={watchedValues.includeSummary}
                        onCheckedChange={checked =>
                          setValue('includeSummary', checked)
                        }
                      />
                    </div>

                    <div className='flex items-center justify-between'>
                      <div>
                        <Label htmlFor='includeRawData'>Include Raw Data</Label>
                        <p className='text-xs text-muted-foreground'>
                          Export detailed data rows
                        </p>
                      </div>
                      <Switch
                        id='includeRawData'
                        checked={watchedValues.includeRawData}
                        onCheckedChange={checked =>
                          setValue('includeRawData', checked)
                        }
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Data Selection */}
          {currentStep === 2 && (
            <div className='space-y-4'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Column Selection</CardTitle>
                  <CardDescription>
                    Choose which columns to include in the export
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-2'>
                    {availableColumns.map((column: string, index: number) => (
                      <div key={index} className='flex items-center space-x-2'>
                        <Checkbox
                          id={`column-${index}`}
                          defaultChecked={true}
                          onCheckedChange={checked => {
                            const currentColumns =
                              watchedValues.filterOptions?.columns || [];
                            if (checked) {
                              setValue('filterOptions.columns', [
                                ...currentColumns,
                                column,
                              ]);
                            } else {
                              setValue(
                                'filterOptions.columns',
                                currentColumns.filter(c => c !== column)
                              );
                            }
                          }}
                        />
                        <Label htmlFor={`column-${index}`} className='text-sm'>
                          {column}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Date Range Filter</CardTitle>
                  <CardDescription>
                    Optionally filter data by date range
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <Label htmlFor='dateFrom'>From Date</Label>
                      <Input
                        id='dateFrom'
                        type='date'
                        {...register('filterOptions.dateRange.from')}
                      />
                    </div>
                    <div>
                      <Label htmlFor='dateTo'>To Date</Label>
                      <Input
                        id='dateTo'
                        type='date'
                        {...register('filterOptions.dateRange.to')}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Delivery Options */}
          {currentStep === 3 && (
            <div className='space-y-4'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>File Naming</CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor='customFileName'>Custom File Name</Label>
                    <Input
                      id='customFileName'
                      {...register('customFileName')}
                      placeholder='Enter custom file name'
                    />
                    <p className='text-xs text-muted-foreground mt-1'>
                      File extension will be added automatically based on format
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className='text-lg flex items-center gap-2'>
                    <Mail className='h-5 w-5' />
                    Email Delivery
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <Label htmlFor='emailEnabled'>Send via Email</Label>
                      <p className='text-sm text-muted-foreground'>
                        Email the exported report to recipients
                      </p>
                    </div>
                    <Switch
                      id='emailEnabled'
                      checked={watchedValues.emailOptions?.enabled}
                      onCheckedChange={checked =>
                        setValue('emailOptions.enabled', checked)
                      }
                    />
                  </div>

                  {watchedValues.emailOptions?.enabled && (
                    <>
                      <div className='flex gap-2'>
                        <Input
                          placeholder='Enter email address...'
                          value={recipientInput}
                          onChange={e => setRecipientInput(e.target.value)}
                          onKeyPress={e =>
                            e.key === 'Enter' &&
                            (e.preventDefault(), addEmailRecipient())
                          }
                        />
                        <Button
                          type='button'
                          onClick={addEmailRecipient}
                          size='sm'
                        >
                          Add
                        </Button>
                      </div>

                      {emailRecipients.length > 0 && (
                        <div className='space-y-2'>
                          <Label>Recipients ({emailRecipients.length})</Label>
                          <div className='flex flex-wrap gap-2'>
                            {emailRecipients.map((email, index) => (
                              <Badge
                                key={index}
                                variant='secondary'
                                className='flex items-center gap-1'
                              >
                                {email}
                                <button
                                  type='button'
                                  onClick={() => removeEmailRecipient(email)}
                                  className='ml-1 hover:text-red-600'
                                >
                                  <X className='h-3 w-3' />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <Label htmlFor='emailSubject'>Email Subject</Label>
                        <Input
                          id='emailSubject'
                          {...register('emailOptions.subject')}
                          placeholder='Email subject'
                        />
                      </div>

                      <div>
                        <Label htmlFor='emailMessage'>Email Message</Label>
                        <textarea
                          id='emailMessage'
                          {...register('emailOptions.message')}
                          className='w-full p-2 border rounded-md'
                          rows={3}
                          placeholder='Email message body'
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className='flex justify-between pt-6 border-t'>
            <div>
              {currentStep > 1 && (
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  Previous
                </Button>
              )}
            </div>

            <div className='flex gap-2'>
              <Button type='button' variant='outline' onClick={onClose}>
                Cancel
              </Button>

              {currentStep < 3 ? (
                <Button
                  type='button'
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  Next
                </Button>
              ) : (
                <Button type='submit' className='flex items-center gap-2'>
                  <Download className='h-4 w-4' />
                  Export Report
                </Button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedExportDialog;
